// src/pages/AgentReferrals/AgentReferrals.jsx
import React, { useMemo, useEffect, useState } from "react";
import { useApp } from "../../../context/AppContext";
import PageHeader from "../../../components/PageHeader/PageHeader";
import DataTable from "../../../components/DataTable/DataTable";
import "./Referrals.css";

const AgentReferrals = () => {
  const {
    currentUser,
    agentReferredCustomers = { agent_referral_code: null, data: [] },
    agentReferredLoading,
    fetchAgentReferredCustomers,
  } = useApp();

  // local search state
  const [search, setSearch] = useState("");

  // ---- AUTO SEARCH WITH DEBOUNCE ----
  useEffect(() => {
    const delay = setTimeout(() => {
      // auto fetch when user stops typing for 500ms
      fetchAgentReferredCustomers(search).catch(() => {});
    }, 500);

    return () => clearTimeout(delay);
  }, [search, fetchAgentReferredCustomers]);

  // ---- INITIAL FETCH ----
  useEffect(() => {
    fetchAgentReferredCustomers().catch(() => {});
  }, [fetchAgentReferredCustomers]);

  // referral code logic
  const referralCode = useMemo(() => {
    if (agentReferredCustomers?.agent_referral_code)
      return agentReferredCustomers.agent_referral_code;

    if (!currentUser) return "N/A";
    if (currentUser.agent_referral_code) return currentUser.agent_referral_code;

    const rawId = String(currentUser._id ?? currentUser.id ?? "");
    const tail =
      rawId.length >= 6 ? rawId.slice(-6).toUpperCase() : rawId.toUpperCase();

    return tail ? `REF-${tail}` : "N/A";
  }, [agentReferredCustomers, currentUser]);

  // normalize table rows
  const referredCustomers = useMemo(() => {
    const arr = Array.isArray(agentReferredCustomers?.data)
      ? agentReferredCustomers.data
      : [];
    return arr.map((c) => ({
      id: c._id ?? c.id ?? "",
      name: c.name ?? "—",
      phone: c.phone ?? c.mobile ?? "—",
      city: c.city ?? c.address?.locality ?? c.address?.city ?? "—",
    }));
  }, [agentReferredCustomers]);

  const columns = [
    { key: "name", label: "Customer Name" },
    { key: "phone", label: "Phone" },
    { key: "city", label: "City" },
  ];

  return (
    <div className="agent-referrals">
      <PageHeader title="Referrals" />

      {/* REFERRAL CODE CARD */}
      <div className="referral-code-section">
        <div className="referral-code-card">
          <h3 className="referral-code-title">Your Referral Code</h3>
          <div className="referral-code-display">
            <span className="referral-code">{referralCode}</span>
            <button
              className="copy-button"
              onClick={() => {
                try {
                  navigator.clipboard.writeText(referralCode);
                  alert("Referral code copied!");
                } catch {
                  alert("Could not copy");
                }
              }}
            >
              📋 Copy
            </button>
          </div>
          <p className="referral-code-note">
            Share this code with customers to earn referral rewards!
          </p>
        </div>
      </div>

      {/* REFERRED CUSTOMERS */}
      <div className="referred-customers-section">
        <h2 className="section-title">
          Referred Customers{" "}
          <small style={{ marginLeft: 8, color: "#666" }}>
            ({referredCustomers.length})
          </small>
        </h2>

        {/* 🔍 SEARCH INPUT ONLY (AUTO RUNS) */}
        <input
          type="search"
          placeholder="Search name / phone / city…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: "8px 10px",
            borderRadius: 6,
            border: "1px solid #ddd",
            marginBottom: 12,
            width: "100%",
            maxWidth: 350,
          }}
        />

        <DataTable
          columns={columns}
          data={referredCustomers}
          searchable={false}
          loading={agentReferredLoading}
          emptyMessage="No referred customers found."
        />
      </div>
    </div>
  );
};

export default AgentReferrals;
